import format from 'date-fns/format'
import distanceInWords from 'date-fns/formatDistance'
import { GridColDef, GridValueFormatterParams } from '@mui/x-data-grid-premium'
import { Gateway } from 'src/api'
import { InnerLink } from 'src/components/ui'
import { Chip, Grid } from '@mui/material'
import { GatewayStateList } from 'src/enums'
import { SensorLostLabel, SensorStagingLabel } from '../sections'

export namespace GatewayColumns {
  export const DeviceId: GridColDef<Gateway> = {
    field: 'uniqueId',
    headerName: 'Unique ID',
    type: 'string',
    sortable: true,
    minWidth: 250,
    renderCell: ({ row }) => {
      const isStagingGateway = row.isStagingGateway
      const isLostGateway = row.state?.name === GatewayStateList.Lost
      const isShortLabel = isStagingGateway && isLostGateway
      const deviceId = row.uniqueId || '-'
      return (
        <Grid container spacing={1} alignItems="center" flexWrap="nowrap" overflow="auto">
          <Grid item>
            <InnerLink to={`/gateways/lorawan/${row.id}`}>{deviceId}</InnerLink>
          </Grid>
          {isStagingGateway ? (
            <Grid item>
              <SensorStagingLabel isShortLabel={isShortLabel} />
            </Grid>
          ) : null}
          {isLostGateway ? (
            <Grid item>
              <SensorLostLabel isShortLabel={isShortLabel} />
            </Grid>
          ) : null}
        </Grid>
      )
    }
  }

  export const IMEI: GridColDef<Gateway> = {
    field: 'deviceId',
    headerName: 'IMEI',
    type: 'string',
    sortable: true,
    width: 150,
    valueGetter: ({ row }) => row.deviceId || '-'
  }

  export const DevEUI: GridColDef<Gateway> = {
    field: 'devEUI',
    headerName: 'DevEUI',
    type: 'string',
    sortable: true,
    width: 150,
    valueGetter: ({ row }) => row.devEUI || '-'
  }

  export const ExternalDeviceId: GridColDef<Gateway> = {
    field: 'externalDeviceId',
    headerName: 'External Device ID',
    type: 'string',
    sortable: true,
    width: 150,
    valueGetter: ({ row }) => row.externalDeviceId || '-'
  }

  export const OnlineStatus: GridColDef<Gateway> = {
    field: 'onlineStatus',
    headerName: 'Online Status',
    type: 'string',
    sortable: true,
    width: 150,
    valueGetter: ({ row }) => row.onlineStatus || '-',
    renderCell: ({ row }) => {
      if (!row.onlineStatus) {
        return '-'
      }

      let color: 'info' | 'success' | 'error' = 'info'
      if (row.onlineStatus === 'online') {
        color = 'success'
      } else if (row.onlineStatus === 'offline') {
        color = 'error'
      }
      return <Chip label={row.onlineStatus} color={color} />
    }
  }

  export const LastPingAt: GridColDef<Gateway> = {
    field: 'currentData.lastPingAt',
    headerName: 'Last Ping At',
    type: 'date',
    sortable: true,
    width: 150,
    valueGetter: ({ row }) => row.currentData?.lastPingAt || null,
    valueFormatter: ({ value }) => (value ? distanceInWords(new Date(value), new Date()) : '-')
  }

  export const Name: GridColDef<Gateway> = {
    field: 'name',
    headerName: 'Name',
    type: 'string',
    sortable: true,
    width: 200,
    valueGetter: ({ row }) => row.name || '-'
  }

  export const State: GridColDef<Gateway> = {
    field: 'state',
    headerName: 'State',
    type: 'string',
    sortable: false,
    width: 150,
    valueGetter: ({ row }) => row.state?.name || ''
  }

  export const Contractor: GridColDef<Gateway> = {
    field: 'gatewayContractors',
    headerName: 'Contractor',
    type: 'string',
    sortable: false,
    width: 200,
    valueGetter: ({ row }) => {
      const { gatewayContractors } = row
      if (!gatewayContractors || gatewayContractors.length === 0) {
        return null
      }
      const activeContractors = gatewayContractors.filter((gc) => gc.isActive)
      if (activeContractors.length === 0) {
        return null
      }

      const lastActive = activeContractors[activeContractors.length - 1]
      return lastActive.contractor ? lastActive.contractor.name : null
    },
    renderCell: ({ row }) => {
      const { gatewayContractors } = row
      if (!gatewayContractors || gatewayContractors.length === 0) {
        return '-'
      }

      const activeContractors = gatewayContractors.filter((gc) => gc.isActive)
      if (activeContractors.length === 0) {
        return '-'
      }
      const lastActive = activeContractors[activeContractors.length - 1]
      return lastActive.contractor ? <InnerLink to={`/contractors/${lastActive.contractor.id}`}>{lastActive.contractor.name}</InnerLink> : '-'
    }
  }

  export const Project: GridColDef<Gateway> = {
    field: 'gatewayProjects',
    headerName: 'Project',
    type: 'string',
    sortable: false,
    width: 200,
    valueGetter: ({ row }) => {
      const { gatewayProjects } = row
      if (!gatewayProjects || gatewayProjects.length === 0) {
        return null
      }
      const activeProject = gatewayProjects.find((pg: any) => pg.isActive)
      if (!activeProject || !activeProject.project) {
        return null
      }
      return activeProject.project ? activeProject.project.name : null
    },
    renderCell: ({ row }) => {
      const { gatewayProjects } = row
      if (!gatewayProjects || gatewayProjects.length === 0) {
        return '-'
      }
      const activeProject = gatewayProjects.find((pg: any) => pg.isActive)
      if (!activeProject || !activeProject.project) {
        return '-'
      }
      return <InnerLink to={`/projects/${activeProject.project.id}`}>{activeProject.project.name || '-'}</InnerLink>
    }
  }

  export const CreatedAt: GridColDef<Gateway> = {
    field: 'createdAt',
    headerName: 'Created At',
    type: 'date',
    sortable: true,
    width: 150,
    valueFormatter: ({ value }: GridValueFormatterParams<string>) => {
      if (!value) return '-'
      return format(new Date(value), 'P')
    }
  }

  export const Comment: GridColDef<Gateway> = {
    field: 'comment',
    headerName: 'Internal comment',
    type: 'string',
    minWidth: 180,
    editable: true
  }

  export const LastKnownContractor: GridColDef<Gateway> = {
    field: 'gatewayContractors',
    headerName: 'Last Known Contractor',
    type: 'string',
    sortable: false,
    width: 200,
    renderCell: ({ row }) => {
      const { gatewayContractors } = row
      if (!gatewayContractors || gatewayContractors.length === 0) {
        return '-'
      }
      // Get the most recent contractor
      const sorted = [...gatewayContractors].sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
      const latest = sorted[0]
      return latest?.contractor ? <InnerLink to={`/contractors/${latest.contractor.id}`}>{latest.contractor.name}</InnerLink> : '-'
    }
  }
}

export const GatewayColumnsDataGrid = GatewayColumns
